import { DeleteOutlined } from '@ant-design/icons';
import { Button, Table } from 'antd';
import { type FormItemProps } from 'antd/es/form';
import { FieldArray, useField } from 'formik';
import { useTranslation } from 'react-i18next';
import { type AdditionalIntegrationParameter } from '../../api/types';
import ResponsiveStyledTable from '../../pages/shared/ResponsiveStyledTable';
import FormFields from '../../themes/admin/Fields/FormFields';
import FormItem from './FormItem';

type IntegrationParameter = AdditionalIntegrationParameter;

export interface IntegrationParameterArrayProps {
    name: string;
    label?: string;
    labelForAdd?: string;
    itemProps?: Omit<FormItemProps, 'label' | 'meta' | 'required' | 'children'>;
    limit?: number;
    disabled?: boolean;
}

const IntegrationParameterFieldArray = ({
    name,
    label,
    labelForAdd,
    itemProps,
    limit,
    disabled,
}: IntegrationParameterArrayProps) => {
    const { t } = useTranslation('dealerForm');

    const [field, meta] = useField({ name });
    const { value } = meta;

    return (
        <FormItem {...itemProps} label={label} name={name}>
            <FieldArray
                {...field}
                name={name}
                render={arrayHelpers => (
                    <ResponsiveStyledTable
                        dataSource={value}
                        pagination={false}
                        rowKey={(row, index: number) => `${name}_${index.toString()}`}
                    >
                        <Table.Column<IntegrationParameter>
                            dataIndex="purpose"
                            render={(value, record, index) => (
                                <FormFields.InputField disabled={disabled} name={`${name}[${index}].purpose`} />
                            )}
                            title={t(
                                'dealerForm:fields.dealerInput.integrationDetails.additionalParameter.purpose.label'
                            )}
                        />
                        <Table.Column<IntegrationParameter>
                            dataIndex="value"
                            render={(value, record, index) => (
                                <FormFields.InputField disabled={disabled} name={`${name}[${index}].value`} />
                            )}
                            title={t(
                                'dealerForm:fields.dealerInput.integrationDetails.additionalParameter.value.label'
                            )}
                        />
                        <Table.Column
                            key="actions"
                            align="center"
                            render={(_, __, index) =>
                                !disabled ? (
                                    <Button
                                        icon={<DeleteOutlined />}
                                        onClick={() => {
                                            arrayHelpers.remove(index);
                                        }}
                                        size="middle"
                                        type="link"
                                    />
                                ) : null
                            }
                            title={t('dealerForm:actions.action')}
                            width={100}
                        />
                    </ResponsiveStyledTable>
                )}
            />
        </FormItem>
    );
};

export default IntegrationParameterFieldArray;
